import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { A11yModule } from '@angular/cdk/a11y';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EMPTY,
  Observable,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

import { AuthService } from '../../_services/auth.service';
import {
  CalculateCiRequest,
  CiCalculationResult,
  CiRecord,
  CiService,
  IntelRecord,
} from '../../_services/ci.service';
import { ToastService } from '../../_services/toast.service';
import { PropertyService } from '../../_services/properties/property.service';

import { ORIENTATION_ADJUSTMENTS, isLandLed } from './config/ci-options.config';
import { pad, stepsFor } from './config/ci-steps.config';
import { CiFormValue, PropertyCategory, StepId } from './models/ci-form.model';
import { CiQuestion } from './models/ci-question.model';
import { answersFromRecord, basisFromResult, buildCalculateRequest, calculationIssues, categoryFromRecord, landRateFromRecord, requestKey, withNeutralChecklist } from './services/ci-api.mapper';
import { CiFormService } from './services/ci-form.service';
import { CiQuestionsResolved } from './services/ci-questions.resolver';

import { CiPropertySearchComponent } from './components/ci-property-search.component';
import { CiPropertyTypeComponent } from './components/ci-property-type.component';
import { CiStepPillsComponent } from './components/ci-step-pills.component';
import { UiCardComponent } from './ui/ui-card.component';
import { UiNoteBarComponent } from './ui/ui-note-bar.component';

import { StepBenchmarkComponent } from './steps/step-benchmark.component';
import { StepBuildingValueComponent } from './steps/step-building-value.component';
import { StepLandAdjustmentsComponent } from './steps/step-land-adjustments.component';
import { StepLandValueComponent } from './steps/step-land-value.component';
import { StepPropertyComponent } from './steps/step-property.component';
import { StepReviewComponent } from './steps/step-review.component';
import { StepTransactionsComponent } from './steps/step-transactions.component';
import { StepUdsRentalComponent } from './steps/step-uds-rental.component';

@Component({
  selector: 'ci-new-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    A11yModule,
    CiPropertyTypeComponent,
    CiStepPillsComponent,
    UiCardComponent,
    UiNoteBarComponent,
    StepPropertyComponent,
    StepTransactionsComponent,
    StepUdsRentalComponent,
    StepBenchmarkComponent,
    StepLandValueComponent,
    StepLandAdjustmentsComponent,
    StepBuildingValueComponent,
    StepReviewComponent,
  ],
  templateUrl: './ci-new-page.component.html',
})
export class CiNewPageComponent {
  private readonly formService = inject(CiFormService);
  private readonly ciApi = inject(CiService);
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly propertyApi = inject(PropertyService);

  private readonly resolved = this.route.snapshot.data['questions'] as CiQuestionsResolved;

  readonly questions = signal<CiQuestion[]>(this.resolved?.questions ?? []);
  readonly loadError = signal<string | null>(this.resolved?.error ?? null);

  readonly form = this.formService.createForm(this.questions());

  readonly currentStepId = signal<StepId>('property');
  /** A requested property type awaiting the save/discard decision. */
  readonly pendingCategory = signal<PropertyCategory | null>(null);
  readonly showLeaveDialog = signal(false);
  private leaveDecision: Promise<boolean> | null = null;
  private resolveLeave: ((leave: boolean) => void) | null = null;
  private savedFormSnapshot: string | null = null;

  private formSnapshot(): string {
    return JSON.stringify(this.form.getRawValue());
  }

  hasUnsavedChanges(): boolean {
    return this.hasSelectedProperty() && this.formSnapshot() !== this.savedFormSnapshot;
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (!this.hasUnsavedChanges()) return true;
    if (this.leaveDecision) return this.leaveDecision;
    this.pendingCategory.set(null);
    this.error.set(null);
    this.showLeaveDialog.set(true);
    this.leaveDecision = new Promise<boolean>((resolve) => this.resolveLeave = resolve);
    return this.leaveDecision;
  }

  keepEditing(): void {
    if (this.savingDraft() || this.generating()) return;
    this.finishLeave(false);
  }

  discardAndLeave(): void {
    if (this.savingDraft() || this.generating()) return;
    this.removeLocalDraft();
    this.finishLeave(true);
  }

  saveAndLeave(): void {
    if (this.savingDraft() || this.generating()) return;
    this.saveDraft(() => this.finishLeave(true));
  }

  private finishLeave(leave: boolean): void {
    const resolve = this.resolveLeave;
    this.resolveLeave = null;
    this.leaveDecision = null;
    this.showLeaveDialog.set(false);
    resolve?.(leave);
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges()) return;
    event.preventDefault();
    event.returnValue = '';
  }

  private localDraftKey(): string {
    return `ci-new-draft:${this.auth.getUserId() ?? 'anonymous'}`;
  }

  private removeLocalDraft(): void {
    if (typeof window === 'undefined') return;
    try { window.localStorage.removeItem(this.localDraftKey()); } catch { /* Navigation can still proceed. */ }
  }

  private restoreLocalDraft(): void {
    if (typeof window === 'undefined' || this.route.snapshot.queryParamMap.has('ciId')) return;
    try {
      const stored = window.localStorage.getItem(this.localDraftKey());
      if (!stored) return;
      const value = JSON.parse(stored) as CiFormValue;
      if (!value.property?.propertyId || !value.benchmarking?.transactions) return;
      this.formService.setTransactions(this.form.controls.benchmarking, value.benchmarking.transactions);
      this.form.patchValue(value);
      this.formService.applyCategoryRules(this.form, value.category);
      this.savedFormSnapshot = this.formSnapshot();
      this.resumedDraft.set(true);
      this.furthestIndex.set(stepsFor(value.category).length);
      this.toast.success('Recovered your earlier browser draft. Save Draft to sync it to CI.');
    } catch {
      this.error.set('The browser draft could not be restored.');
    }
  }
  readonly furthestIndex = signal(1);
  /** A persisted record can always be revisited step-by-step while editing. */
  readonly resumedDraft = signal(false);
  /** A calculate request is in flight (automatic or triggered by an action). */
  readonly calculating = signal(false);
  readonly savingDraft = signal(false);
  private readonly persistedRecord = signal<CiRecord | null>(null);
  private readonly knownRecords = signal<IntelRecord[]>([]);
  private readonly recordsLoaded = signal(false);
  readonly generatedProperty = computed(() => {
    const property = this.formValue().property;
    const saved = this.persistedRecord();
    return (saved?.status === 'GENERATED' && (saved.propertyId ? saved.propertyId === property.propertyId : saved.propertyName === property.propertyName)) ||
      this.knownRecords().some((record) => record.status === 'Completed' &&
        (record.propertyId ? record.propertyId === property.propertyId :
          record.property.trim().toLowerCase() === property.propertyName.trim().toLowerCase() &&
          (record.microMarket ?? '').trim().toLowerCase() === property.microMarket.trim().toLowerCase()));
  });
  readonly generating = signal(false);
  readonly error = signal<string | null>(null);
  /** Backend calculation for `resultKey`, or the persisted record after save/generate. */
  readonly result = signal<CiCalculationResult | CiRecord | null>(null);
  /** Request the current `result` was calculated from; null once it is used up. */
  private readonly resultKey = signal<string | null>(null);
  /** Request the GENERATED record was created from — blocks a duplicate generate. */
  private readonly generatedKey = signal<string | null>(null);
  /** Backend message for the automatic calculation, shown on the review step. */
  readonly calculationError = signal<string | null>(null);

  readonly busy = computed(
    () => this.calculating() || this.savingDraft() || this.generating(),
  );

  private readonly rawValue = toSignal(
    this.form.valueChanges.pipe(startWith(this.form.getRawValue()), debounceTime(100)),
    { initialValue: this.form.getRawValue() },
  );

  readonly formValue = computed<CiFormValue>(() => {
    this.rawValue();
    return this.form.getRawValue() as CiFormValue;
  });

  readonly category = computed(() => this.formValue().category);

  /** Four steps for land and houses, five for units. */
  readonly steps = computed(() => stepsFor(this.category()));

  readonly currentStep = computed(
    () => this.steps().find((s) => s.id === this.currentStepId()) ?? this.steps()[0],
  );

  readonly currentIndex = computed(
    () => this.steps().findIndex((s) => s.id === this.currentStepId()) + 1,
  );

  readonly isFirstStep = computed(() => this.currentIndex() === 1);
  readonly isLastStep = computed(() => this.currentIndex() === this.steps().length);

  /** "01. Plot Information" — number comes from position in the active flow. */
  readonly cardHeading = computed(
    () => `${pad(this.currentIndex())}. ${this.currentStep().heading}`,
  );

  private readonly request = computed<CalculateCiRequest | null>(() =>
    buildCalculateRequest(this.formValue(), this.questions(), this.auth.getUserId()),
  );
  /** Plain-language list of what still blocks a complete calculation. */
  readonly calculationIssues = computed(() => calculationIssues(this.formValue(), this.questions()));
  private readonly currentKey = computed(() => requestKey(this.request()));

  /** True when `result` was calculated from exactly what is on the form now. */
  private readonly resultIsFresh = computed(
    () => !!this.result() && this.resultKey() !== null && this.resultKey() === this.currentKey(),
  );

  /** The backend result only while it matches the form — never a stale figure. */
  readonly freshResult = computed(() => (this.resultIsFresh() ? this.result() : null));

  /** Apartments need the checklist questions; plots and houses do not use them. */
  private hasChecklist(): boolean {
    return isLandLed(this.category()) || this.questions().length > 0;
  }

  /**
   * Benchmark figures for steps 2 and 4. Derived from the backend result; for
   * the non-transaction sources the analyst-entered rate is the benchmark.
   */
  readonly derivedBasis = computed(() =>
    basisFromResult(
      this.result(),
      this.formValue(),
      this.form.controls.benchmark.controls.baseBenchmarkPsf.value,
    ),
  );

  readonly completedSteps = computed(() => {
    this.rawValue();
    const done = new Set<StepId>();
    for (const step of this.steps()) {
      if (step.id === 'review') continue;
      if (this.formService.isStepValid(this.form, step.id)) done.add(step.id);
    }
    return done as ReadonlySet<StepId>;
  });

  readonly currentStepValid = computed(() => {
    this.rawValue();
    return this.formService.isStepValid(this.form, this.currentStepId());
  });

  readonly canGenerate = computed(() => {
    this.rawValue();
    return (
      this.formService.isFlowValid(this.form) &&
      this.hasChecklist() &&
      // Same inputs already generated — a second click would only duplicate the record.
      (this.generatedKey() === null || this.generatedKey() !== this.currentKey())
    );
  });

  constructor() {
    this.ciApi.getDashboardData().pipe(takeUntilDestroyed()).subscribe({
      next: (data) => {
        this.knownRecords.set(data.recentIntel);
        this.recordsLoaded.set(true);
      },
      error: () => this.error.set('Could not check existing CI records. Reload before saving.'),
    });
    this.restoreLocalDraft();
    this.prefillFromRecord();
    this.watchForCalculation();
  }

  /** Dashboard View/Edit actions pass `?ciId=` to load the saved record into the editor. */
  private prefillFromRecord(): void {
    const ciId = this.route.snapshot.queryParamMap.get('ciId');
    if (!ciId) return;

    this.ciApi.getCiRecordById(ciId).subscribe({
      next: (rec) => {
        this.persistedRecord.set(rec);
        const f = this.form.controls;

        // Restore the property type first: it decides the flow, and the
        // required fields and steps below depend on it.
        const category = categoryFromRecord(rec);
        f.category.setValue(category);
        this.formService.applyCategoryRules(this.form, category);
        f.property.patchValue({
          propertyId: rec.propertyId ?? '',
          propertyName: rec.propertyName ?? '',
          microMarket: rec.microMarket ?? '',
          cluster: rec.cluster ?? '',
          size: rec.size ?? null,
          configuration: rec.configuration ?? '',
          // Some saved records have no listing name; rebuild it the same way
          // picking a property does ("Property · configuration").
          listingUnitName: rec.listingUnitName
            || (category === 'primary-project'
              ? ''
              : [rec.propertyName, rec.configuration].filter(Boolean).join(' · ')),
          quotedPrice: rec.quotedUnitPrice ?? null,
        });
        f.property.markAsDirty();

        // The record already holds its calculated figures — show them straight away
        // instead of a blank review; the recalculation below replaces them once it lands.
        this.result.set(rec);

        const landRate = landRateFromRecord(rec);
        if (rec.landValue) {
          // Skip nulls so fields a record predates keep their form defaults.
          const saved = Object.fromEntries(
            Object.entries(rec.landValue).filter(([, v]) => v != null),
          );
          const orientation = rec.landValue.orientation
            ?? Object.keys(ORIENTATION_ADJUSTMENTS).find(
              (k) => Math.abs(ORIENTATION_ADJUSTMENTS[k] - (rec.landValue!.orientationAdjustment ?? 0)) < 1e-9,
            )
            ?? 'east';
          f.landValue.patchValue({
            ...saved,
            orientation,
            landRatePsf: rec.landValue.landRatePsf ?? landRate,
          });
          f.landValue.markAsDirty();
        } else if (landRate !== null) {
          // Records saved before land values were stored carry only the rate.
          f.landValue.controls.landRatePsf.setValue(landRate);
          f.landValue.markAsDirty();
        }
        if (rec.buildingValue) {
          const b = rec.buildingValue;
          // Records from the replacement-cost model have no class or rate; those stay blank.
          f.buildingValue.patchValue({
            builtUpArea: b.builtUpArea ?? null,
            ageYears: b.ageYears ?? null,
            constructionClass: b.constructionClass ?? '',
            buildingRatePsf: b.buildingRatePsf ?? null,
            floors: b.floors ?? null,
          });
          f.buildingValue.markAsDirty();
        }

        // A land-led record's only row is the Land Rate, not a real transaction.
        this.formService.setTransactions(
          f.benchmarking,
          (landRate !== null ? [] : rec.lastFiveTransactions ?? [])
            .filter((t) => t?.price != null)
            .map((t) => ({
              psf: t.price,
              source: t.source ?? '',
              date: this.toInputDate(t.date),
            })),
        );

        f.udsRental.patchValue({
          udsSqft: rec.uds ?? null,
          currentRatePsf: rec.currentUdsRatePerSqft ?? null,
          monthlyRent: rec.averageRentPerMonth ?? null,
          trophyOverride: !!rec.trophyOverride,
        });
        f.benchmark.controls.answers.patchValue(answersFromRecord(rec, this.questions()));
        f.benchmark.markAsDirty();
        f.benchmarking.markAsDirty();
        f.udsRental.markAsDirty();

        // A saved draft is intentionally incomplete, but it is still a
        // resumable workflow. Let the analyst inspect and edit every step
        // instead of trapping them on Property because furthestIndex starts at
        // one for a brand-new CI.
        this.furthestIndex.set(this.steps().length);
        this.resumedDraft.set(true);
        this.savedFormSnapshot = this.formSnapshot();
      },
      error: (err: { message?: string }) =>
        this.error.set(err?.message || 'Could not load the selected property.'),
    });
  }

  /** API dates (ISO or "dd Mon yyyy") → the yyyy-mm-dd the date inputs hold. */
  private toInputDate(value?: string | null): string {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    const d = new Date(`${value} UTC`);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }

  /**
   * Recalculates on the backend whenever the inputs change, so steps 2–5 always
   * show the API's figures. switchMap drops a superseded in-flight request, and
   * an unchanged payload is never sent twice.
   */
  private watchForCalculation(): void {
    toObservable(this.request)
      .pipe(
        debounceTime(600),
        // Checklist answers come on a later step; until then preview with neutral ones
        // so the benchmark is available from the transactions step onward.
        map((req) => (req ? withNeutralChecklist(req) : null)),
        filter((req): req is CalculateCiRequest => !!req),
        distinctUntilChanged((a, b) => requestKey(a) === requestKey(b)),
        filter((req) => requestKey(req) !== this.resultKey()),
        tap(() => {
          this.calculating.set(true);
          this.calculationError.set(null);
        }),
        switchMap((req) =>
          this.ciApi.calculate(req).pipe(
            tap((result) => this.applyResult(result, requestKey(req))),
            catchError((err: { message?: string }) => {
              // A stale result next to a fresh error would be misleading.
              this.result.set(null);
              this.resultKey.set(null);
              this.calculationError.set(err?.message ?? 'The calculation failed. Try again.');
              return EMPTY;
            }),
            finalize(() => this.calculating.set(false)),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  private applyResult(result: CiCalculationResult, key: string | null): void {
    this.result.set(result);
    this.resultKey.set(key);

    // Keep the (required) base-rate control in step with the backend benchmark.
    const benchmark = this.form.controls.benchmark;
    const transactionsSource = this.form.controls.benchmarking.controls.source.value === 'transactions';
    if (transactionsSource && !benchmark.controls.overrideBase.value && result.benchmarkPsf > 0) {
      benchmark.controls.baseBenchmarkPsf.setValue(Math.round(result.benchmarkPsf));
    }
  }

  /* ── Navigation ───────────────────────────────────────────────────────── */

  next(): void {
    const step = this.currentStepId();
    if (!this.resumedDraft() && !this.formService.isStepValid(this.form, step)) {
      this.formService.markStepTouched(this.form, step);
      return;
    }
    if (step === 'land-value') this.syncLandRateToBenchmark();
    const nextStep = this.steps()[this.currentIndex()];
    if (!nextStep) return;
    this.currentStepId.set(nextStep.id);
    this.furthestIndex.update((i) => Math.max(i, this.currentIndex()));
    this.scrollToTop();
  }

  /** Land-led flows establish their adjustment base in the Land Value step. */
  private syncLandRateToBenchmark(): void {
    if (!isLandLed(this.category())) return;
    const benchmark = this.form.controls.benchmark.controls;
    const landRate = this.form.controls.landValue.controls.landRatePsf.value;
    // A plot's base rate is read-only on the next step, so it always follows the Land Rate.
    const followsLandRate = this.category() === 'plot-land' || !benchmark.overrideBase.value;
    if (followsLandRate && landRate && landRate > 0) {
      benchmark.baseBenchmarkPsf.setValue(landRate);
    }
  }

  back(): void {
    const prev = this.steps()[this.currentIndex() - 2];
    if (!prev) return;
    this.currentStepId.set(prev.id);
    this.scrollToTop();
  }

  goToStep(id: StepId): void {
    const index = this.steps().findIndex((s) => s.id === id) + 1;
    if (index === 0 || (!this.resumedDraft() && index > this.furthestIndex())) return;
    this.currentStepId.set(id);
    this.scrollToTop();
  }

  blockedMessage(): string {
    switch (this.currentStepId()) {
      case 'transactions':
        return 'Add at least one transaction, or set the base rate manually.';
      case 'benchmark':
      case 'adjustments':
        return 'Set the base rate to continue.';
      default:
        return 'Complete the required fields to continue.';
    }
  }

  /* ── Category switching ───────────────────────────────────────────────── */

  /**
   * Switching type rewrites the flow, so the wizard resets to step one rather
   * than stranding the analyst on a step the new flow does not contain.
   */
  onCategoryChange(category: PropertyCategory): void {
    if (category === this.category()) return;

    if (this.hasSelectedProperty()) {
      this.pendingCategory.set(category);
      return;
    }

    this.discardAndSwitchCategory(category);
  }

  saveAndSwitchCategory(): void {
    const category = this.pendingCategory();
    if (!category || this.savingDraft() || this.generating()) return;
    this.saveDraft(() => this.discardAndSwitchCategory(category));
  }

  discardAndSwitchCategory(category = this.pendingCategory()): void {
    if (!category) return;

    // A property type has a different calculation flow. Start it with a clean
    // form so values from the prior type cannot accidentally be reused.
    this.removeLocalDraft();
    this.savedFormSnapshot = null;
    this.form.reset();
    this.form.controls.category.setValue(category);
    this.formService.applyCategoryRules(this.form, category);
    this.form.markAsPristine();
    this.form.markAsUntouched();

    this.currentStepId.set('property');
    this.furthestIndex.set(1);
    this.resumedDraft.set(false);
    this.pendingCategory.set(null);
    this.result.set(null);
    this.resultKey.set(null);
    this.calculationError.set(null);
    this.error.set(null);
  }

  keepCurrentCategory(): void {
    this.pendingCategory.set(null);
  }

  private hasSelectedProperty(): boolean {
    return !!this.form.controls.property.controls.propertyId.value;
  }

  isLandLed(): boolean {
    return isLandLed(this.category());
  }

  /* ── Form plumbing ────────────────────────────────────────────────────── */

  addSimilarProperty(): void {
    this.formService.addSimilarProperty(this.form);
  }

  removeSimilarProperty(index: number): void {
    this.formService.removeSimilarProperty(this.form, index);
  }

  onOverrideToggled(manual: boolean): void {
    if (manual) return;
    // Back to the derived rate — the backend result is the source for it.
    const benchmark = this.form.controls.benchmark.controls.baseBenchmarkPsf;
    const derived = this.result()?.benchmarkPsf;
    if (derived && derived > 0) benchmark.setValue(Math.round(derived));
  }

  /* ── Actions ──────────────────────────────────────────────────────────── */

  /** Validates the whole flow; on failure jumps to the first incomplete step. */
  private validateFlow(): boolean {
    this.formService.markFlowTouched(this.form);

    if (this.formService.isFlowValid(this.form) && this.hasChecklist()) {
      this.error.set(null);
      return true;
    }

    const firstIncomplete = this.steps().find(
      (s) => s.id !== 'review' && !this.formService.isStepValid(this.form, s.id),
    );
    this.error.set(
      !this.hasChecklist()
        ? 'The CI checklist questions have not loaded. Reload the page.'
        : firstIncomplete
          ? `Some required fields are incomplete in "${firstIncomplete.heading}".`
          : 'Some required fields are incomplete.',
    );
    if (firstIncomplete) this.goToStep(firstIncomplete.id);
    return false;
  }

  /**
   * Reuses the stored calculation when the form has not changed since;
   * otherwise asks the backend for a fresh one. Emits the result to persist.
   */
  private ensureFreshCalculation(forDraft = false): Observable<CiCalculationResult> {
    // Read the live controls: clicking Save can happen before the preview debounce.
    const rawRequest = buildCalculateRequest(
      this.form.getRawValue() as CiFormValue, this.questions(), this.auth.getUserId(),
    );
    const request = rawRequest && forDraft ? withNeutralChecklist(rawRequest) : rawRequest;
    const key = requestKey(request);
    const current = this.result();
    if (current && key !== null && this.resultKey() === key) return of(current);

    if (!request) {
      this.error.set('Size, quoted price and at least one transaction price are required to calculate.');
      return EMPTY;
    }

    this.calculating.set(true);
    return this.ciApi.calculate(request).pipe(
      tap((result) => this.applyResult(result, key)),
      finalize(() => this.calculating.set(false)),
    );
  }

  saveDraft(afterSave?: () => void): void {
    if (!this.recordsLoaded()) {
      this.error.set('Wait for existing CI records to load, or reload if loading failed.');
      return;
    }
    if (this.generatedProperty()) {
      this.error.set('Generated CI cannot be moved to draft. Edit the generated CI instead.');
      return;
    }
    if (this.savingDraft() || this.generating()) return;
    if (!this.hasSelectedProperty()) {
      this.error.set('Select a property before saving a draft.');
      return;
    }
    // Drafts use the existing calculate → draft API flow, without requiring
    // every wizard step to be complete. Never report a browser-only save.
    if (!buildCalculateRequest(this.form.getRawValue() as CiFormValue, this.questions(), this.auth.getUserId())) {
      this.error.set('Add a size, quoted price and at least one transaction price before saving to CI.');
      return;
    }

    this.savingDraft.set(true);
    this.ensureFreshCalculation(true)
      .pipe(
        switchMap((calc) =>
          this.ciApi.saveDraft({
            tempCalculationId: calc._id,
            userId: this.auth.getUserId() ?? undefined,
            propertyId: this.form.controls.property.controls.propertyId.value || undefined,
            ciRecordId: this.persistedRecord()?._id,
          }),
        ),
        finalize(() => this.savingDraft.set(false)),
      )
      .subscribe({
        next: (record) => {
          this.onPersisted(record, 'Draft saved successfully.');
          afterSave?.();
        },
        error: (err: unknown) => this.onActionError(err, 'Saving the draft failed. Try again.'),
      });
  }

  generate(): void {
    if (this.busy() || !this.validateFlow()) return;

    const key = this.currentKey();
    this.generating.set(true);
    this.ensureFreshCalculation()
      .pipe(
        switchMap((calc) =>
          this.ciApi.generateCi({
            tempCalculationId: calc._id,
            userId: this.auth.getUserId() ?? undefined,
            propertyId: this.form.controls.property.controls.propertyId.value || undefined,
            ciRecordId: this.persistedRecord()?._id,
          }),
        ),
        finalize(() => this.generating.set(false)),
      )
      .subscribe({
        next: (record) => {
          this.generatedKey.set(key);
          this.onPersisted(record, 'CI generated successfully.');
          void this.router.navigate(['/admin/property/cip']);
        },
        error: (err: unknown) => this.onActionError(err, 'Generating the CI failed. Try again.'),
      });
  }

  private onPersisted(record: CiRecord, message: string): void {
    // The temp calculation is spent; the record replaces it on screen and any
    // further save/generate recalculates from scratch.
    this.savedFormSnapshot = this.formSnapshot();
    this.removeLocalDraft();
    this.persistedRecord.set(record);
    this.result.set(record);
    this.resultKey.set(null);
    this.error.set(null);
    this.toast.success(message);
    this.attachGeneratedReportToProperty(record);
  }

  /**
   * Keep the generated report with the property as well as in the CI
   * collection. The website can then render the report from its normal
   * property-details response without needing an admin dashboard lookup.
   */
  private attachGeneratedReportToProperty(record: CiRecord): void {
    if (record.status !== 'GENERATED') return;

    const propertyId = record.propertyId
      ?? this.form.controls.property.controls.propertyId.value;
    if (!propertyId) return;

    // Deliberately omit internal ownership and temporary-calculation metadata
    // from the property payload consumed by the website.
    // The Supply Override amount and its reason are internal too; analyst
    // notes quote the reason, so they stay with the admin record.
    const { userId, tempCalculationId, analystNotes, ...report } = record;
    const publicReport = {
      ...report,
      landValue: report.landValue
        ? { ...report.landValue, supplyOverrideValue: null, supplyOverrideReason: null }
        : report.landValue,
      landAssessment: report.landAssessment
        ? { ...report.landAssessment, supplyOverrideValue: null, supplyOverrideReason: null }
        : report.landAssessment,
    };
    this.propertyApi.updateProperty(propertyId, {
      ciRecordId: record._id,
      ciUpdatedAt: record.updatedAt,
      ciIntelligence: publicReport,
    }).subscribe({
      error: () => this.toast.warning('CI was generated, but could not yet be linked to the property.'),
    });
  }

  private onActionError(err: unknown, fallback: string): void {
    const message = (err as { message?: string })?.message || fallback;
    this.error.set(message);
    this.toast.error(message);
  }

  cancel(): void {
    void this.router.navigate(['/admin/property/cip']);
  }

  private scrollToTop(): void {
    if (typeof window === 'undefined') return;
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
  }
}
